import { fetchPollQuestions, getPollAnswers } from 'store/polling'
import { times, reduce, merge } from 'lodash'
import { getPollStatusText, getPollType } from 'utils/polling'
import { Roles } from 'constants/index'

export const downloadAttendanceReportInCSV = (
  userRole,
  attendeesAttendance,
  confName,
  conferenceDates
) => {
  const headings = [
    'Last Name',
    'First Name',
    'Position',
    'Email',
    'Phone Number',
    'Institution',
    ...conferenceDates
  ]

  userRole === Roles.SUPER_ADMIN && headings.push('Conference')

  const values = attendeesAttendance?.map((attendee) => {
    const data = [
      attendee?.lastName?.toString(),
      attendee?.firstName?.toString(),
      attendee?.position?.toString(),
      attendee?.email?.toString(),
      attendee?.phoneNumber?.toString(),
      attendee?.company?.toString()
    ]
    attendee.attendance.forEach((attendance) => {
      data.push(attendance.attendanceFound ? 'Present' : '')
    })
    userRole === Roles.SUPER_ADMIN && data.push(confName?.toString())
    return data
  })
  const rows = [headings, ...values]
  downloadCSVFile(rows, `${confName}_attendance_report`)
}

export const downloadAttendeesInCSV = (attendees, conference) => {
  const headings = [
    'Last Name',
    'First Name',
    'Position',
    'Email',
    'Phone Number',
    'Institution',
    'Conference'
  ]
  const values = attendees?.map((item) => [
    item?.lastName?.toString(),
    item?.firstName?.toString(),
    item?.position?.toString(),
    item?.email?.toString(),
    item?.phoneNumber?.toString(),
    item?.company?.toString(),
    conference?.toString()
  ])
  const rows = [headings, ...values]
  downloadCSVFile(rows, 'conference_attendees')
}

export const downloadPollResultsInCSV = async (poll) => {
  const Headings = [
    'Poll Title',
    'Poll Status',
    'Poll Type',
    'Question Title',
    'Option A',
    'Option B',
    'Option C',
    'Option D',
    'Option E',
    'Option F',
    'Option G',
    'Option A Count',
    'Option B Count',
    'Option C Count',
    'Option D Count',
    'Option E Count',
    'Option F Count',
    'Option G Count',
    'Total Count'
  ]
  const pollQuestions = await fetchPollQuestions(poll?.id)
  const pollAnswers = await getPollAnswers(poll?.id)

  const csvData = pollQuestions.map((question) => {
    const questionAnswers = pollAnswers?.filter((doc) => doc.questionId === question?.id)
    const totalCount = questionAnswers?.length ?? 0

    const optionsWithCount = question?.options?.map((option, idx) => {
      const optionCount =
        questionAnswers?.filter((doc) => doc?.optId === option?.optId)?.length ?? 0
      return {
        [`option${idx + 1}`]: option.opt,
        [`voteCount${idx + 1}`]: optionCount
      }
    })

    const optionsArr = pushDataMultipleTimes(optionsWithCount, 7) // max 7 options
    const mergedObject = reduce(
      optionsArr,
      (result, obj) => {
        return merge(result, obj)
      },
      {}
    )

    return {
      question: question.title,
      ...mergedObject,
      totalCount
    }
  })

  const values = csvData?.map((item) => [
    poll?.title,
    getPollStatusText(poll?.status).status,
    getPollType(poll),
    item?.question.toString(),
    item?.option1.toString(),
    item?.option2.toString(),
    item?.option3.toString(),
    item?.option4.toString(),
    item?.option5.toString(),
    item?.option6.toString(),
    item?.option7.toString(),
    item.voteCount1,
    item.voteCount2,
    item.voteCount3,
    item.voteCount4,
    item.voteCount5,
    item.voteCount6,
    item.voteCount7,
    item.totalCount
  ])

  const rows = [Headings, ...values]
  const fileName = `${poll?.speaker ?? 'Anonymous'}_${poll?.associatedTitle}`
  const trimmedFileName = trimFilename(fileName)
  downloadCSVFile(rows, trimmedFileName)
}

const getEmptyOption = (idx) => {
  return {
    [`option${idx + 1}`]: 'N/A',
    [`voteCount${idx + 1}`]: 0
  }
}

const pushDataMultipleTimes = (dataArray, maxLimit) => {
  const arrLength = dataArray.length
  if (!arrLength) {
    return []
  } else if (arrLength < maxLimit) {
    const time = maxLimit - arrLength
    times(time, (idx) => {
      const option = getEmptyOption(idx + arrLength)
      dataArray.push(option)
    })
    return dataArray
  } else return dataArray
}

export const downloadBadgeSubjectsInCSV = (subjectType, subjects) => {
  const headings = [
    'Counter',
    'FullName',
    'Credential',
    'Organization',
    'Position/Title',
    'City',
    'State',
    'Country',
    'Email',
    'Phone'
  ]

  const values = subjects?.map((badge) => {
    return [
      badge?.counter?.toString(),
      badge?.FullName?.toString(),
      badge?.Credential?.toString(),
      badge?.Organization?.toString(),
      badge?.Position_Title?.toString(),
      badge?.City?.toString(),
      badge?.State?.toString(),
      badge?.Country?.toString(),
      badge?.Email?.toString(),
      badge?.Phone?.toString()
    ]
  })
  const rows = [headings, ...values]
  downloadCSVFile(rows, `${subjectType}_badges_data`)
}

export const downloadCSVFile = (rows, fileName) => {
  const csvContent = rows
    .map((row) =>
      row
        .map((field) =>
          typeof field === 'string'
            ? `"${field.replace(/"/g, '""')}"`
            : typeof field === 'number'
            ? field
            : ''
        )
        .join(',')
    )
    .join('\n')
  const link = document.createElement('a')
  link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURI(csvContent))
  link.setAttribute('download', `${fileName}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const trimFilename = (filename) => {
  const maxLength = 255

  if (filename.length > maxLength) {
    return filename.slice(0, maxLength)
  }

  return filename
}
